import React from 'react';
import {
  Component,
  Position,
  PuzzleButton,
  PuzzleInput
} from "puzzle";
import _ from 'lodash';
import classNames from "classnames";
import {
  commonLocalizationGetter,
  getMatchingDisplayName, getPureMatchingDisplayName,
  getStatusesLocalizationPath,
  getUnixDate
} from "../../util/Util";
import { getMappedSubmissionStatuses } from "../../util/Util";
import { MaskedTextBoxChangeEvent } from "@progress/kendo-react-inputs/dist/npm/maskedtextbox/MaskedTextBoxProps";

type FILTER = {
  filterName?: string
  filterId?: string
  fileName?: string
  quickSearchInputText?: string
  submissionNameInputText?: string
  submissionCount?: number
  jobPhaseCount?: number
  searchData?: string,
  pastDueDateComboBox?: string
};

type SEARCH_BAR_PROPS = {
  initializeUserData: any,
  isDrawerOpen: boolean,
  isClientView: boolean
  setSearchBarState: Function //used to notify parent components about searchbar changes
}

type SEARCH_BAR_STATE = {
  allFilters: any[],
  // if expanded filter is in edit mode
  editedFilterName: string,
  showDropDownForm: boolean,
  mainSearchValue: string,
  filter: FILTER,
  selectedTag: number | null,
  customControlValues: any
}

export class SearchBar extends Component<SEARCH_BAR_PROPS, SEARCH_BAR_STATE> {
  timer = 0;
  searchBar: any = React.createRef();
  searchBarToggleButton: any = React.createRef();
  tooltip: any;

  state: SEARCH_BAR_STATE = {
    allFilters: [],
    editedFilterName: '',
    mainSearchValue: '',
    showDropDownForm: false,
    filter: {},
    selectedTag: null,
    customControlValues: {}
  };

  /**
   * Add new filter to the SearchBar
   * Used initially when filter selection is made
   * from the outside of the SearchBar component
   *
   * @param filterData
   * @param preserveSubmissionId
   */
  addFilterToState = (filterData: any, preserveSubmissionId: boolean = false) => {
    delete filterData.quickSearchInputText; //quickSearchInputText should not appear in the search bar w/ other applier filters
    let customControlValues: any = _.reduce(filterData.customControlValues, function (obj: any, param) {
      obj[param.name] = param.value;
      return obj;
    }, {});
    Object.assign(filterData, filterData.submissionStatusValues ? { submissionStatusValues: getMappedSubmissionStatuses(filterData.submissionStatusValues, 'SUBMISSION_STATUSES_MAP_UI') } : null);
    if (!preserveSubmissionId) {
      delete filterData.submissionId;
    }
    let filter = _.omit(filterData, ['customControlValues', 'index', 'ascending', 'folder'])
    Object.assign(filter, filter.pastDueDateComboBox ? { pastDueDateComboBox: filterData.pastDueDateComboBox } : null);
    //@ts-ignore
    this.setState({
      filter,
      ...(!_.isEmpty(customControlValues) && { customControlValues: customControlValues })
    }, this.afterFilterChange);
  };

  /**
   * On searchbar changes notifies parent component if there are active filters/searches
   */
  afterFilterChange = (): void => {
    if (this.props.setSearchBarState) {
      let active = !_.isEmpty(this.state.filter) || !_.isEmpty(this.state.customControlValues) || (this.state.mainSearchValue !== "");
      this.props.setSearchBarState(active);
    }
  };

  /**
   * Load all filters from data base
   *
   * @param filters
   */
  addAllFilters = (filters: any) => {
    this.setState({
      allFilters: filters
    });
  };

  /**
   * Delete favorite filter from search bar input
   */
  clearFavoriteFilter = () => {
    this.setState({ filter: {} });
    this.dispatch('clearFilterMainGridAndGetNewData');
  };

  /**
   * Remove tag pair from the SearchBar
   * Should filter grid if dropdown is closed or if the last tag was removed
   */
  clearItemFromSearchBar = (filterProp: string, custom?: boolean) => {
    switch (filterProp) {
      case 'taskNameInputText':
        filterProp = 'submissionNameInputText';
        break;
      case 'quickSearchInputTextSupplier':
        filterProp = 'quickSearchInputText';
        break;
      default:
        break;
    }
    let updatedFilter = !custom ? { ...this.state.filter } : { ...this.state.customControlValues };
    delete (updatedFilter as any)[filterProp];
    //@ts-ignore
    this.setState({
      ...(!custom ? { filter: { ...updatedFilter } } : { customControlValues: { ...updatedFilter } }),
      mainSearchValue: ''
    }, () => {
      let customControlValues = this.createCustomControlValuesForPost();
      this.dispatch('getSubmissionCount', Position.end, {
        mainGridFilter: {
          ...(_.omit(this.state.filter, 'filterId')),
          ...{ customControlValues }
        },
        fireMainGridFilter: true
      })
      this.afterFilterChange();
    });
  };

  /**
   * Remove filter and mainSearchValue from state in order to clear search bar
   */
  clearSearchBar = (withRefresh = true) => {
    this.setState({
      editedFilterName: '',
      filter: {},
      customControlValues: {},
      mainSearchValue: ''
    }, () => {
      if (withRefresh) {
        this.dispatch('clearFilterMainGridAndGetNewData');
      }
      this.afterFilterChange();
    });
  };

  /**
   * Creates property and places all custom attributes filters criteria in it.
   */
  createCustomControlValuesForPost = () => {
    const { customControlValues: customAttributeFilter } = this.state;
    let customControlValues = _.isEmpty(customAttributeFilter) ? [] : _.transform(customAttributeFilter, (acc: any, key, val) => {
      acc.push({ name: val, value: key });
    }, []);
    return customControlValues;
  };

  /**
   * Opens create search dialog
   */
  createNewSearch = () => {
    let searchData = _.omit(this.state.filter, ['filterName', 'filterId']);
    const cleanedData = this.getCleanedFilterData(searchData);
    this.setState({ ...cleanedData, mainSearchValue: '' }, () => {
      this.dispatch('openAdvancedSearchDialog', '', { modeData: { ...searchData, mode: 'clone' } })
    });
  };

  /**
   * Filters Main Grid after user start typing on main search field
   */
  filterMainGridWithDelay = () => {
    clearTimeout(this.timer);
    (this.timer as any) = setTimeout(() => {
      this.dispatch('filterMainGrid', '', {
        mainGridFilter: { quickSearchInputText: this.state.mainSearchValue },
        skipAdvancedSearchLoad: true,
        clearQuickFilters: true
      })
      this.afterFilterChange();
    }, 300);
  };

  /**
   * Returns true if all criteria evaluated to true.
   * e.g. Dates has value, filterId should not be displayed
   * Might be expanded to some other conditions
   *
   * @param filter
   * @param key
   */
  getTagValidity = (filter: FILTER, key: string) => {
    const hiddenProperties = ['filterId', 'color'];
    let validKey = !hiddenProperties.includes(key);
    if (validKey && (_.includes(key.toLowerCase(), 'date'))) {
      validKey = (filter as any)[key] !== null;
    }
    return validKey;
  };

  /**
   * Synchronize filter form changes with the SearchBar state
   * in order to preset real time updates in the tag value
   */
  onChange = (event: MaskedTextBoxChangeEvent) => {
    this.dispatch('checkboxSelectionClear');
    const mainSearchValue: any = event.target.value;
    this.setState({ mainSearchValue }, () => this.filterMainGridWithDelay());
  };

  getCleanedFilterData = (filter: FILTER) => {
    filter = { ...filter }
    const submissionName = this.state.mainSearchValue;
    if (submissionName) {
      filter.quickSearchInputText = submissionName;
    }

    delete filter.filterName;
    delete filter.submissionCount;
    delete filter.jobPhaseCount;
    let customControlValues = filter.searchData ? _.reduce(JSON.parse(filter.searchData)?.customControlValues, function (obj: any, param) {
      obj[param.name] = param.value;
      return obj;
    }, {}) : this.state.customControlValues;
    const originalFilters = filter.searchData ? _.omit(this.decodeEditData(filter), 'customControlValues') : filter;
    return {
      filter: originalFilters,
      ...(customControlValues && { customControlValues })
    }
  };
  /**
   * Open drop down form and break favourite filter tag
   * into individual key & value pair tags
   */
  openDropDownForm = () => {
    const filter = { ...this.state.filter };
    const cleanedData = this.getCleanedFilterData(filter);
    this.setState({ ...cleanedData, mainSearchValue: '' }, () => {
      this.dispatch('openCloneFilterDialog', '', {
        dashboardFilter: false,
        createFilterTitle: this.translate('createFilter'),
        filterData: {
          ...this.state.filter,
          ...(filter.searchData && { searchData: filter.searchData })
        }
      })
    })
  };

  /**
   * Decodes and converts data to proper format for edit filter.
   */
  decodeEditData = (data: any) => {
    let decodedFilterData = {
      ...data,
      ...this.removeUnsupportedFilterKeys(JSON.parse(data.searchData))
    };
    decodedFilterData = _.omit(decodedFilterData, ['isFavorite', 'searchData']);
    Object.assign(decodedFilterData,
      decodedFilterData.submissionStatusValues ? { submissionStatusValues: getMappedSubmissionStatuses(decodedFilterData.submissionStatusValues, 'SUBMISSION_STATUSES_MAP_UI') } : null
    );
    return decodedFilterData
  };

  /**
   * Update the tag's value when the data
   * from inside of the form changes
   */
  onFormDataChange = (filterProp: string, filterValue: string | number | Date | null, custom?: any) => {
    filterValue = _.isDate(filterValue) ? getUnixDate(filterValue) : filterValue;
    let filter = !custom ? { ...this.state.filter } : { ...this.state.customControlValues };
    const notEmpty = !_.isNull(filterValue) && String(filterValue).length && (_.isBoolean(filterValue) ? filterValue : true);
    if (notEmpty) {
      filter = {
        ...filter,
        [filterProp]: filterValue
      };
    } else {
      delete (filter as any)[filterProp];
    }

    this.setState((prevState: SEARCH_BAR_STATE) => ({
      ...prevState,
      ...(custom ? { customControlValues: filter } : { filter })
    }), () => {
      let customControlValues = _.isEmpty(this.state.customControlValues) ? [] : _.transform(this.state.customControlValues, function (rez, key, val) {
        //@ts-ignore
        rez.push({ name: val, value: key });
      }, []);
      this.dispatch('getSubmissionCount', Position.end, {
        mainGridFilter: {
          ...(_.omit({ ...this.state.filter }, 'filterId')),
          ...(customControlValues.length && { customControlValues })
        }
      })
    });
  };

  onFormSubmit = (e: any) => {
    e.preventDefault();
    this.setState((prevState: any) => ({
      ...prevState,
      filter: {
        ...prevState.filter,
        quickSearchInputText: prevState.mainSearchValue
      }
    }), () => {
      this.searchHandler();
    });
  };

  /**
   * Prepare data for search. Gets data from filter and transform it to proper data
   * for submission search
   * @param data
   */
  prepareDataForSearch = (data: any) => {
    let decodedFilterData = {
      ...JSON.parse(data.searchData)
    };
    decodedFilterData = this.removeUnsupportedFilterKeys(decodedFilterData);
    return decodedFilterData
  };

  /**
   * Removes unsupported filter keys from object
   * @param filter
   */
  removeUnsupportedFilterKeys = (filter: any) => {
    const supportedFilter = { ...filter };
    const supportedKeys =
      [
        'submissionNameInputText', 'fromDueDate', 'toDueDate', 'targetLanguageValues',
        'submissionStatusValues', 'projectValues', 'phasesRemoteCombo', 'sourceLanguageValues',
        'submissionOwnerValues', 'fromDateCreated', 'toDateCreated', 'fileNameInputText',
        'customControlValues', 'pastDueDateComboBox', 'userSubmissions', 'includeHidden',
        'quoteStatuses', 'submissionId', 'taskStatusValues', 'wordCountFrom', 'wordCountTo',
        'fromPhaseStartDate', 'toPhaseStartDate', 'fromPhaseDueDate', 'toPhaseDueDate', 'phaseStatuses', 'priority',
        'vendorBudgetStatuses'
        // 'awardedVendorsCombo', 'vendorsCombo', 'phaseStatuses':
      ];
    return _.pick(supportedFilter, supportedKeys)
  };

  /**
   * Render bar component
   *
   * If there are no filtering parameters - render input that will
   * filter the grid based on the submissionName property
   *
   * When there are filtering parameters - render wrapper div that
   * contains key and value pairs displayed as tags
   */
  renderSearchBar = () => {
    const hasFilters = !_.isEmpty(_.omit(this.state.filter, ['isFavorite', 'searchData', 'color', 'filterId']));
    const hasCustomFilters = !_.isEmpty(this.state.customControlValues);
    const inputClassForQA = classNames(
      'tp-searchbar-input FAKE-QA-CLASS',
      {
        'empty': _.isEmpty(this.state.mainSearchValue),
        'filled': !_.isEmpty(this.state.mainSearchValue)
      }
    );

    const { isClientView } = this.props;
    let inputSearchText = isClientView ? "searchBySubmissionNames" : "searchByTaskNames";
    return hasFilters || hasCustomFilters ? (
      <div className="tp-searchbar-placeable">

        <i
          aria-hidden={true}
          className="tp-icon tp-search k-hbox k-align-items-center"
          data-cy="search-icon"
        />
        <ul className={'tp-searchbar-scrollbale-wrap k-reset'}>
          {this.renderPairs()}
        </ul>
      </div>
    ) : (
      <form onSubmit={this.onFormSubmit} className="tp-searchbar-form">
        <PuzzleInput
          aria-label={this.translate(inputSearchText)}
          inputClassName={inputClassForQA}
          name="quickSearchInputText"
          onChange={this.onChange}
          placeholder={this.translate(inputSearchText)}
          searchField
          value={this.state.mainSearchValue}
        />
      </form>
    );
  };

  /**
   * Render tag pairs
   *
   * If the filter parameters come from the outside of SearchBar component
   * it will come with filterName & filterId properties. In that case we
   * render the one pair showing the filter name and favourites icon (heart)
   *
   * If the filter is not favourite, display key and value pairs as a tag
   */
  renderPairs = () => {
    const filter = { ...this.state.filter };
    let pairs: any = [];
    const filterName = filter.filterName;
    if (filterName) {
      pairs.push(
        <li
          className="tp-searchbar-placeable-item-favorite"
          key={`tp-searchbar-placeable-item-${filter.filterId}`}
        >
          <span>{filterName}</span>
          <button
            aria-label={this.translate('SRL-clearFilter', { filterName: filterName })}
            className="tp-clear-filter"
            onClick={this.clearFavoriteFilter}
          >
            <i className={'tp-icon tp-close '} aria-hidden="true" />
          </button>
        </li>
      );
    } else {
      const keys = Object.keys(filter);
      pairs = keys.map(this.renderTag.bind(this, filter, false));
    }

    const customFilter = { ...this.state.customControlValues };
    if (!_.isEmpty(customFilter)) {
      const customFilterKeys = Object.keys(customFilter);
      const customAttributePairs = customFilterKeys.map(this.renderTag.bind(this, customFilter, true));
      pairs = [...pairs, ...customAttributePairs];
    }

    return pairs;
  };

  /**
   * Render each individual filter tag
   */
  renderTag = (filter: FILTER, custom: boolean, key: string, index: number) => {
    const { isClientView } = this.props;
    let el = null;
    const tagCondition = this.getTagValidity(filter, key);
    if (tagCondition) {
      const tagId = `tp-searchbar-placeable-item-${key}:${(filter as any)[key]}`;
      const isTagSelected = this.state.selectedTag === index;
      const placeableItemCls = classNames(
        'tp-searchbar-placeable-item',
        {
          'tp-selected': isTagSelected
        }
      );
      const hideDotsAndValueForKeys = ['includeHidden', 'userSubmissions'];
      const hideDotsAndValue = hideDotsAndValueForKeys.includes(key);
      const value = this.renderTagValue(filter, key);
      const ariaLabelSuffix =
        [
          'submissionNameInputText',
          'fromDateCreated',
          'fromDueDate',
          'toDateCreated',
          'toDueDate'
        ].includes(key) || custom ? value : value?.props?.children;
      if (key === 'submissionNameInputText' && !isClientView) {
        key = 'taskNameInputText';
      }
      if (key === 'quickSearchInputText' && !isClientView) {
        key = 'quickSearchInputTextSupplier';
      }
      el = (
        <li
          className={placeableItemCls}
          key={tagId}
          onClick={this.selectTag.bind(this, index)}
        >
          <div className="tp-searchbar-placeable-item-key">{this.translate(key)} {!hideDotsAndValue && ':'}</div>
          {!hideDotsAndValue &&
            <div className="tp-searchbar-placeable-item-value">{value}</div>
          }
          <PuzzleButton
            iconClass={'tp-icon tp-x'}
            fillMode={'flat'}
            aria-label={this.translate("SRL-clearSearchFilter", { key: this.translate(key), value: ariaLabelSuffix })}
            className={'tp-clear-filter'}
            onClick={this.clearItemFromSearchBar.bind(this, key, custom)}
            size={'small'}
            themeColor={'primary'}
          >
          </PuzzleButton>
        </li>
      );
    }
    return el;
  };

  /**
   * Format the tag value in case the value comes
   * as a Date object
   */
  renderTagValue = (filter: FILTER, key: string) => {
    let returnValue = (filter as any)[key];
    switch (key) {
      case 'fromDateCreated':
      case 'fromDueDate':
      case 'fromPhaseDueDate':
      case 'fromPhaseStartDate':
      case 'toDateCreated':
      case 'toDueDate':
      case 'toPhaseDueDate':
      case 'toPhaseStartDate':
        returnValue = this.translateByNamespace('date.dateWithTime', {
          date: new Date(returnValue)
        });
        break;
      case 'awardedVendorsCombo':
      case 'phasesRemoteCombo':
      case 'phaseStatuses':
      case 'submissionOwnerValues':
      case 'sourceLanguageValues':
      case 'targetLanguageValues':
      case 'projectValues':
        returnValue = _.isArray(returnValue) ? returnValue : [returnValue];
        returnValue = getMatchingDisplayName(returnValue, key, this.props.initializeUserData);
        break;
      case 'quoteStatuses':
      case 'submissionStatusValues':
      case 'taskStatusValues':
      case 'vendorBudgetStatuses':
      case 'priority':
        if (key === 'priority') {
          key = 'priorityList';
        }
        const path = getStatusesLocalizationPath(key);
        returnValue = _.isArray(returnValue) ? returnValue : [returnValue];
        returnValue = !['quoteStatuses', 'vendorBudgetStatuses'].includes(key) ? getPureMatchingDisplayName(returnValue, key, this.props.initializeUserData) : returnValue;
        returnValue = returnValue.map((item: any) => commonLocalizationGetter(`${path}.${item}`)).join(', ');
        break;
      case 'pastDueDateComboBox':
        returnValue = this.translate(`${filter.pastDueDateComboBox}`)
        break
      default:
        break;
    }
    return returnValue;
  };

  /**
   * Select appropriate tag by its index
   */
  selectTag = (index: number) => {
    this.setState({ selectedTag: index })
  };


  searchHandler = () => {
    const customControlValues = this.createCustomControlValuesForPost();
    this.dispatch('filterMainGrid', '', {
      mainGridFilter: {
        ...this.state.filter,
        ...(customControlValues.length && { customControlValues })
      }
    });
  };

  /**
   * Updates filter data before post
   *
   * TODO update any with proper type
   *
   * @param dataWithUpdates
   */
  updateDataForPost = (dataWithUpdates: any) => {
    const { filterId } = this.state.filter;
    const { editedFilterName } = this.state;
    Object.assign(dataWithUpdates, { filterName: editedFilterName });
    return { filterData: dataWithUpdates, filterId };
  };


  renderBody() {
    const { allFilters } = this.state;
    const { isDrawerOpen, isClientView } = this.props;

    const clearAllFilters = classNames(
      'tp-clear-all-filters',
      {
        'tp-visible': (((!_.isEmpty(this.state.filter) || !_.isEmpty(this.state.customControlValues)) && this.state.filter.quickSearchInputText !== '') || this.state.mainSearchValue !== '')
      }
    );

    return (
      <>
        <div className={'tp-searchbar-content'}>
          <div
            className={'tp-searchbar-combobox'}
            ref={this.searchBar}
          >
            {this.renderSearchBar()}
            <PuzzleButton
              aria-label={this.translate('SRL-clearAllFilter')}
              titleText={this.translate('SRL-clearAllFilter')}
              onClick={this.clearSearchBar.bind(this, true)}
              iconClass="tp-icon tp-x"
              fillMode="flat"
              size={'small'}
              className={clearAllFilters}
              tooltipPosition={'bottom'}
              withTooltip
            />
            {isClientView &&
              <PuzzleButton
                ref={(el: any) => this.searchBarToggleButton = el}
                fillMode="flat"
                iconClass="tp-icon tp-filter"
                aria-label={this.translate('SRL-openFilterForm')}
                titleText={this.translate('SRL-openFilterForm')}
                className={"tp-searchbar-trigger"}
                onClick={() => this.openDropDownForm()}
                size={'small'}
                tooltipPosition={'bottom'}
                withTooltip
              />}
            {!isClientView &&
              <PuzzleButton
                iconClass={'tp-icon tp-filter'}
                withTooltip
                className={'pd-searchbar-advanced-search-btn'}
                fillMode={'flat'}
                titleText={this.translate('advancedSearch')}
                size={'small'}
                tooltipPosition={'bottom'}
                onClick={this.createNewSearch}
              />
            }
          </div>
          {
            !!allFilters.length && !isDrawerOpen &&
            <PuzzleButton
              themeColor={'primary'}
              fillMode="link"
              className={'tp-favorite-dropdown'}
              onClick={() => {
                this.setState({
                  editedFilterName: '',
                  filter: {},
                  customControlValues: {},
                  mainSearchValue: ''
                }, () => {
                  this.dispatch('openSavedFiltersDialog', '', {
                    allFilters: allFilters
                  })
                });
              }}
            >
              {this.translate('savedFilters')}
            </PuzzleButton>
          }
        </div>
      </>
    );
  }
}

export default React.memo(SearchBar);
